// machineAlarmUtils — pure helpers for per-machine alarms (contract §10, issue
// sun#3519). Level vocabulary is the SAME as sensorAlarmUtils (ERR / WAR / INF, with
// unknown/absent degrading to ERR — CE2: never under-report severity), so the level
// normalisation is reused from there rather than duplicated.
import { normalizeLevel, SEVERITY_ORDER } from './sensorAlarmUtils'

// Visual treatment per normalised level, reusing the module's severity palette
// (byGroup.css): ERR = red, WAR = amber, INF = idle cyan. Both the badge/chip colour
// class and the left severity rail class travel together.
const LEVEL_VISUAL = {
  ERR: { chipClass: 'mcx-alarm-chip--ERR', railClass: 'mcx-rail-red' },
  WAR: { chipClass: 'mcx-alarm-chip--WAR', railClass: 'mcx-rail-amber' },
  INF: { chipClass: 'mcx-alarm-chip--INF', railClass: 'mcx-rail-cyan' }
}

/**
 * @param {string|undefined} level
 * @returns {{chipClass: string, railClass: string}}
 */
function alarmLevelVisual (level) {
  return LEVEL_VISUAL[normalizeLevel(level)] ?? LEVEL_VISUAL.ERR
}

/**
 * Normalises the snapshot-embedded `alarmSummary` ({ err, war, inf } lowercase counts,
 * §10.1) to the ERR/WAR/INF buckets, coercing missing/NaN values to 0.
 * @param {{err?: number, war?: number, inf?: number}|undefined} summary
 * @returns {{ERR: number, WAR: number, INF: number}}
 */
function normalizeAlarmSummary (summary) {
  const source = summary ?? {}
  return {
    ERR: Number(source.err) || 0,
    WAR: Number(source.war) || 0,
    INF: Number(source.inf) || 0
  }
}

/**
 * Total active-alarm count across every severity (err + war + inf).
 * @param {object|undefined} summary
 * @returns {number}
 */
function alarmSummaryTotal (summary) {
  const counts = normalizeAlarmSummary(summary)
  return counts.ERR + counts.WAR + counts.INF
}

/**
 * ERR-only count. The CARD badge (PO, 2026-07-10) flags ERRORS only — a WAR/INF alarm
 * must NOT make the machine read as errored on the grid; those still show in full in the
 * detail alarms panel. So the badge count and its presence key off this, not the total.
 * @param {object|undefined} summary
 * @returns {number}
 */
function alarmSummaryErrorCount (summary) {
  return normalizeAlarmSummary(summary).ERR
}

/**
 * Highest severity present in the summary (ERR > WAR > INF), driving the badge colour.
 * @param {object|undefined} summary
 * @returns {'ERR'|'WAR'|'INF'|''} '' when the summary has no active alarms.
 */
function alarmSummaryTopLevel (summary) {
  const counts = normalizeAlarmSummary(summary)
  for (const level of SEVERITY_ORDER) {
    if (counts[level] > 0) return level
  }
  return ''
}

export { alarmLevelVisual, normalizeAlarmSummary, alarmSummaryTotal, alarmSummaryErrorCount, alarmSummaryTopLevel }
